/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {Grid, Stack, Tooltip, Typography, Box} from '@mui/material';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {BookOpen, FileText, Phone, Zap} from 'lucide-react';
import {ResourceCountItem} from './ResourceCountItem';
import {FeatureFlagSet, FeatureFlags} from '../../../../flags';

interface AgentDetailsCellProps {
  abilities: Array<{name?: string; title?: string}>;
  faqs: Array<{question?: string; title?: string}>;
  documents?: Array<{filename?: string; name?: string; title?: string}>;
  phoneNumbers?: string[] | null;
}

export const AgentDetailsCell = ({
  abilities = [],
  faqs = [],
  documents = [],
  phoneNumbers = [],
}: AgentDetailsCellProps) => {
  const flags = useFlags<FeatureFlagSet>();
  const showPhoneNumbers = flags[FeatureFlags.testAgent];

  // Ensure phoneNumbers is always an array
  const phoneNumbersArray = phoneNumbers || [];
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          {abilities && abilities.length > 0 && (
            <ResourceCountItem
              icon={Zap}
              count={abilities.length}
              items={abilities}
              color={'primary.main'}
              singularLabel={'Ability'}
              pluralLabel={'Abilities'}
              tooltipTitle={'Abilities'}
            />
          )}

          {faqs && faqs.length > 0 && (
            <ResourceCountItem
              icon={BookOpen}
              count={faqs.length}
              items={faqs}
              color={'warning.main'}
              singularLabel={'FAQ'}
              pluralLabel={'FAQs'}
              tooltipTitle={'FAQs'}
            />
          )}

          {documents && documents.length > 0 && (
            <ResourceCountItem
              icon={FileText}
              count={documents.length}
              items={documents}
              color={'success.main'}
              singularLabel={'Document'}
              pluralLabel={'Documents'}
              tooltipTitle={'Documents'}
            />
          )}
        </Stack>
      </Grid>
      {showPhoneNumbers && phoneNumbersArray.length > 0 && (
        <Grid item xs={6}>
          <Tooltip
            title={
              <Box component={'span'} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                {phoneNumbersArray.map((number, index) => (
                  <Typography key={index} variant={'body2'}>
                    {number}
                  </Typography>
                ))}
              </Box>
            }
            PopperProps={{
              onClick: (e) => e.stopPropagation(),
            }}
          >
            <Typography variant={'body2'} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
              <Box sx={{color: 'info.main'}}>
                <Phone size={16} />
              </Box>
              <Typography component={'span'} fontWeight={'medium'} color={'info.main'}>
                {phoneNumbersArray.length}
              </Typography>
              {phoneNumbersArray.length === 1 ? ' Number' : ' Numbers'}
            </Typography>
          </Tooltip>
        </Grid>
      )}
    </Grid>
  );
};
